import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useLikesStore } from './likesStore';
import * as shopApi from '../services/shopApi';
import analytics from '../utils/analytics';
import { getErrorMessage as getApiErrorMessage } from '../utils/errorHandler';

// Customer types
interface Customer {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  is_active: boolean;
  default_address_line1: string | null;
  default_address_line2: string | null;
  default_city: string | null;
  default_state: string | null;
  default_zip: string | null;
  created_at: string;
}

interface CustomerUpdateRequest {
  first_name?: string;
  last_name?: string;
  phone?: string;
  default_address_line1?: string;
  default_address_line2?: string;
  default_city?: string;
  default_state?: string;
  default_zip?: string;
}

interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
}

interface CustomerRegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
}

interface CustomerLoginRequest {
  email: string;
  password: string;
}

interface CustomerAuthState {
  customer: Customer | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  register: (data: CustomerRegisterRequest) => Promise<void>;
  login: (credentials: CustomerLoginRequest) => Promise<void>;
  logout: () => void;
  getCurrentCustomer: () => Promise<void>;
  updateProfile: (data: CustomerUpdateRequest) => Promise<void>;
  changePassword: (data: PasswordChangeRequest) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

export const useCustomerAuthStore = create<CustomerAuthState>()(
  persist(
    (set) => ({
      customer: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      register: async (data: CustomerRegisterRequest) => {
        try {
          set({ isLoading: true, error: null });

          const response = await shopApi.customerRegister(data);
          const { access_token, customer } = response.data;

          localStorage.setItem('customer_access_token', access_token);

          analytics.identify(`customer_${customer.id}`, { email: customer.email, type: 'customer' });

          set({
            customer,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          const errorMessage = getApiErrorMessage(error);
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
            customer: null,
          });
          throw new Error(errorMessage);
        }
      },

      login: async (credentials: CustomerLoginRequest) => {
        try {
          set({ isLoading: true, error: null });

          const response = await shopApi.customerLogin(credentials);
          const { access_token, customer } = response.data;

          localStorage.setItem('customer_access_token', access_token);

          analytics.identify(`customer_${customer.id}`, { email: customer.email, type: 'customer' });

          set({
            customer,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Merge and sync wishlist after login
          useLikesStore.getState().mergeAndSync();
        } catch (error: any) {
          const errorMessage = getApiErrorMessage(error);
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
            customer: null,
          });
          throw new Error(errorMessage);
        }
      },

      logout: () => {
        analytics.reset();
        localStorage.removeItem('customer_access_token');
        set({
          customer: null,
          isAuthenticated: false,
          error: null,
        });
      },

      getCurrentCustomer: async () => {
        const token = localStorage.getItem('customer_access_token');
        if (!token) {
          set({
            customer: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          return;
        }

        try {
          set({ isLoading: true });

          const response = await shopApi.getCustomerProfile();

          set({
            customer: response.data,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          // Token invalid or expired
          localStorage.removeItem('customer_access_token');
          set({
            customer: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      updateProfile: async (data: CustomerUpdateRequest) => {
        try {
          set({ isLoading: true, error: null });

          const response = await shopApi.updateCustomerProfile(data);

          set({
            customer: response.data,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          // If token expired/invalid, clear auth state
          const status = error.response?.status;
          if (status === 401 || error.message?.includes('expired') || error.message?.includes('Invalid')) {
            localStorage.removeItem('customer_access_token');
            set({
              customer: null,
              isAuthenticated: false,
              isLoading: false,
              error: 'Session expired. Please log in again.',
            });
          } else {
            const errorMessage = getApiErrorMessage(error);
            set({
              error: errorMessage,
              isLoading: false,
            });
          }
          throw error;
        }
      },

      changePassword: async (data: PasswordChangeRequest) => {
        try {
          set({ isLoading: true, error: null });

          await shopApi.changeCustomerPassword(data);

          set({ isLoading: false, error: null });
        } catch (error: any) {
          const errorMessage = getApiErrorMessage(error);
          set({
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },

      clearError: () => set({ error: null }),

      setLoading: (loading: boolean) => set({ isLoading: loading }),
    }),
    {
      name: 'customer-auth-storage',
      partialize: (state) => ({
        customer: state.customer,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
